<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { required, helpers } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  board: { type: Object, default: null },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);

const formState = reactive({ name: '', isDefault: false });

const v$ = useVuelidate(
  {
    name: {
      required: helpers.withMessage(
        () => t('KANBAN.SETTINGS.BOARD_FORM.ERRORS.NAME'),
        required
      ),
    },
  },
  formState
);

const isEdit = computed(() => !!props.board);
const isLoading = ref(false);

const resetForm = () => {
  formState.name = props.board?.name || '';
  formState.isDefault = props.board?.is_default || false;
  v$.value.$reset();
};

watch(() => props.board, resetForm, { immediate: true });

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isLoading.value = true;
  try {
    if (isEdit.value) {
      await store.dispatch('kanban/boards/update', {
        id: props.board.id,
        name: formState.name,
        is_default: formState.isDefault,
      });
      useAlert(t('KANBAN.SETTINGS.BOARD_API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('kanban/boards/create', { name: formState.name });
      useAlert(t('KANBAN.SETTINGS.BOARD_API.CREATE_SUCCESS'));
    }
    dialogRef.value.close();
  } catch (error) {
    useAlert(
      t(
        isEdit.value
          ? 'KANBAN.SETTINGS.BOARD_API.UPDATE_ERROR'
          : 'KANBAN.SETTINGS.BOARD_API.CREATE_ERROR'
      )
    );
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="resetForm"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <Input
        id="board-name"
        v-model="formState.name"
        :label="t('KANBAN.SETTINGS.BOARD_FORM.NAME_LABEL')"
        :placeholder="t('KANBAN.SETTINGS.BOARD_FORM.NAME_PLACEHOLDER')"
        :message="v$.name.$error ? v$.name.$errors[0]?.$message : ''"
        :message-type="v$.name.$error ? 'error' : 'info'"
        @blur="v$.name.$touch()"
      />
      <label class="flex items-center gap-2 text-body-main text-n-slate-12">
        <input v-model="formState.isDefault" type="checkbox" />
        {{ t('KANBAN.SETTINGS.BOARD_FORM.IS_DEFAULT_LABEL') }}
      </label>
      <div class="flex items-center justify-end gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="t('KANBAN.SETTINGS.BOARD_FORM.CANCEL')"
          @click="dialogRef.close()"
        />
        <NextButton
          type="submit"
          :label="
            t(
              isEdit
                ? 'KANBAN.SETTINGS.BOARD_FORM.UPDATE'
                : 'KANBAN.SETTINGS.BOARD_FORM.CREATE'
            )
          "
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
